import { Capacitor } from '@capacitor/core';
import { ConnectionStatus, Network } from '@capacitor/network';
import {
  analyticsService,
  EventNames,
  pushNotificationService,
} from '@taleemabad/db';
import { AlertDialog, NoInternetHeader } from '@taleemabad/dsm/components';
import { PropsWithChildren, useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { SupportWidget } from './support-widget';

enum NotificationAction {
  StudentResults = 'studentResults',
}

/**
 * This component handles one-time initialization logic, like adding event listeners.
 * DO NOT include any state, re-rendering logic, or anything that triggers a re-render in this component.
 * As the parent component, it will not re-render throughout the app's lifecycle.
 */
export const ParentLayout = ({ children }: PropsWithChildren) => {
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [networkStatus, setNetworkStatus] = useState<boolean | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    // add logic to handle actions when a notification is received while the app is in the foreground here
    pushNotificationService.addNotificationReceivedListener((event) => {
      const action = (event.notification?.data as { action?: string })?.action;
      if (action === NotificationAction.StudentResults) {
        setShowResultPopup(true);
      }
    });

    // add logic to handle actions when a notification action is performed while the app is in the background and the user clicks on the notification here
    pushNotificationService.addNotificationActionPerformedListener((event) => {
      const action = (event.notification?.data as { action?: string })?.action;
      if (action === NotificationAction.StudentResults) {
        navigate('/report-cards');
      }
    });
  }, []);

  const handleStudentResultNotificationClick = () => {
    setShowResultPopup(false);
    analyticsService.trackEvent(EventNames.resultsNotificationClicked);
    navigate('/report-cards');
  };

  const handleStudentResultNotificationDismiss = () => {
    setShowResultPopup(false);
    analyticsService.trackEvent(EventNames.resultsNotificationDismissed);
  };
  useEffect(() => {
    const checkIsNetworkAvailable = async () => {
      const status = (await Network.getStatus()).connected;
      setNetworkStatus(status ? null : false);
    };
    if (Capacitor.isNativePlatform()) {
      Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
        setNetworkStatus(status.connected);
      });
      checkIsNetworkAvailable();

      return () => {
        Network.removeAllListeners();
      };
    }
  }, []);

  return (
    <>
      {Capacitor.isNativePlatform() && (
        <NoInternetHeader state={networkStatus} />
      )}
      <AlertDialog
        title="Student results available"
        description="Your Grade 3 English Assessment 2 results are now available in the app."
        showPopup={showResultPopup}
        primaryButtonText="See Results"
        secondaryButton={true}
        secondaryButtonText="Not now"
        hasBackdrop={false}
        onPrimaryButtonClick={handleStudentResultNotificationClick}
        onSecondaryButtonClick={handleStudentResultNotificationDismiss}
        onClosePopup={() => setShowResultPopup(false)}
      />
      {children}
      <Outlet />
      <SupportWidget />
    </>
  );
};
